import { renderAmount, renderDate } from '../../../..';
import { mdiPlus } from '@mdi/js';
import _ from 'lodash';
import React from 'react';
import { IInvoiceDetailProps } from './invoice-detail.types';
import * as S from './invoice-detail.styles';
import * as Card from './timeline-detail-card.styles';

/**
 * InvoiceDetail Component
 * 
 * Displays detailed information about an invoice.
 * Supports both Provider and Support app-specific display requirements.
 * 
 * Provider: Shows invoice details, items, transactions, payment plans, and create plan button
 * Support: Shows invoice info (ID, status, type, stripe info) and items only
 * 
 * Used by both Provider and Support apps in their timeline views.
 */
export const InvoiceDetail: React.FunctionComponent<IInvoiceDetailProps> = (
    props: IInvoiceDetailProps
): React.ReactElement => {
    const { invoice, appType, guarantor, transactions = [], payments = [], paymentPlans = [], onCreatePaymentPlan } = props;

    // Adjustment items carry a negative `price` and are shown as discounts, not hidden.
    const invoiceItems = invoice.items || [];

    const isAdjustment = (item: { itemId?: string }): boolean =>
        item.itemId === 'ENTER_ADJUSTMENT' || item.itemId === 'PROVIDER_ADJUSTMENT';

    // The generic backend name "Adjustment" adds nothing; a custom memo (e.g. "Patient Loyalty Adjustment") does.
    const renderAdjustmentLabel = (item: { name?: string; quantity?: number }): string => {
        const memo = item.name && item.name !== 'Adjustment' ? ` - ${item.name}` : '';
        const quantity = item.quantity && item.quantity > 1 ? `${item.quantity} x ` : '';
        return `${quantity}Adjustments${memo}`;
    };

    // Build code-to-payment-item map for Provider
    const codeItemMap = React.useMemo(() => {
        if (appType !== 'provider') {
            return {};
        }
        const items = _.flatten(_.map(payments, payment => payment.items));
        return _.reduce(
            _.compact(items),
            (acc: Record<string, any>, item: any) => {
                if (!item?.procedureCode) {
                    return acc;
                }
                acc[item?.procedureCode] = item;
                return acc;
            },
            {},
        );
    }, [appType, payments]);

    // Render payment plans helper
    const renderPaymentPlans = (plans: typeof paymentPlans) => {
        return plans.map(plan => {
            const { amountPerCycle, intervalCount, intervalSpan } = plan;
            const interval = intervalCount !== 1 ? intervalCount : '';
            const suffix = intervalCount !== 1 ? 's' : '';
            return (
                <div key={plan.id!}>
                    ${_.get(amountPerCycle, 'amount')} payment every {interval} {_.toLower(intervalSpan)}
                    {suffix}
                </div>
            );
        });
    };

    if (appType === 'provider') {
        // Provider display: Full invoice details with transactions and payment plans
        const itemRows = _.map(invoiceItems, (item) => {
            if (isAdjustment(item)) {
                return (
                    <S.ExpandTR key={item.id}>
                        <S.ExpandTD colSpan={3}>{renderAdjustmentLabel(item)}</S.ExpandTD>
                        <S.ExpandTD align="right">{renderAmount(item.price)}</S.ExpandTD>
                    </S.ExpandTR>
                );
            }

            const paymentItem = item.itemId ? codeItemMap[item.itemId] : undefined;
            const fromDos = paymentItem?.fromDos ? renderDate(paymentItem.fromDos) : undefined;
            const throughDos = paymentItem?.throughDos ? renderDate(paymentItem.throughDos) : undefined;
            const dates = _.compact([fromDos, throughDos]).join(' - ');

            return (
                <React.Fragment key={item.id}>
                    <S.ExpandTR>
                        <S.ExpandTD colSpan={2}>
                            {item.quantity} {item.itemId} {item.name}
                        </S.ExpandTD>
                        <S.ExpandTD>{!!dates && dates}</S.ExpandTD>
                        <S.ExpandTD align="right">{renderAmount(item.price)}</S.ExpandTD>
                    </S.ExpandTR>
                    <S.ExpandTR>
                        <S.ExpandTD colSpan={2}>{item.description}</S.ExpandTD>
                        <S.ExpandTD />
                    </S.ExpandTR>
                    {item.discount && (
                        <S.ExpandTR>
                            <S.ExpandTD />
                            <S.ExpandTD>Discount</S.ExpandTD>
                            <S.ExpandTD align="right">{renderAmount(item.discount)}</S.ExpandTD>
                        </S.ExpandTR>
                    )}
                    {item.tax && (
                        <S.ExpandTR>
                            <S.ExpandTD />
                            <S.ExpandTD>Tax</S.ExpandTD>
                            <S.ExpandTD align="right">{renderAmount(item.tax)}</S.ExpandTD>
                        </S.ExpandTR>
                    )}
                </React.Fragment>
            );
        });

        const transactionRows = _.map(transactions, transaction => (
            <S.ExpandTR key={transaction.id}>
                <S.ExpandTD>{renderDate(transaction.created)}</S.ExpandTD>
                <S.ExpandTD align="right">{renderAmount(transaction.amount)}</S.ExpandTD>
                <S.ExpandTD>{transaction.refund?.amount?.amount || '--'}</S.ExpandTD>
                <S.ExpandTD>{transaction.billedByDescriptor}</S.ExpandTD>
                <S.ExpandTD>{transaction.status}</S.ExpandTD>
                <S.ExpandTD>{transaction.memo}</S.ExpandTD>
            </S.ExpandTR>
        ));

        return (
            <Card.CdPanel $embedded>
                <Card.CdSectionCard $embedded>
                    <S.ExpandTable>
                        <S.ExpandTBody key={invoice.id}>
                        <S.ExpandTR>
                            <S.ExpandTH>Invoice Date</S.ExpandTH>
                            <S.ExpandTD>{renderDate(invoice.invoiceDate)}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Invoice Type</S.ExpandTH>
                            <S.ExpandTD>{invoice.invoiceType}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Invoice Status</S.ExpandTH>
                            <S.ExpandTD>{invoice.invoiceStatus} </S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Due Date</S.ExpandTH>
                            <S.ExpandTD>{renderDate(invoice.dueDate)} </S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Amount</S.ExpandTH>
                            <S.ExpandTD>{renderAmount(invoice.amount)} </S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH> Paid Amount</S.ExpandTH>
                            <S.ExpandTD>{renderAmount(invoice.paidAmount)} </S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH> Remaining Amount</S.ExpandTH>
                            <S.ExpandTD>{renderAmount(invoice.remainingAmountDue)} </S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH> Memo</S.ExpandTH>
                            <S.ExpandTD>{invoice.memo} </S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Description</S.ExpandTH>
                            <S.ExpandTD>{invoice.description}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Payment Plans</S.ExpandTH>
                            <S.ExpandTD>{paymentPlans.length ? renderPaymentPlans(paymentPlans) : '--'}</S.ExpandTD>
                        </S.ExpandTR>
                    </S.ExpandTBody>
                </S.ExpandTable>
                </Card.CdSectionCard>

                <Card.CdSectionCard $embedded>
                    <S.ExpandTable>
                        <S.ExpandTHead>
                            <tr>
                                <S.ExpandTH colSpan={2}>Invoice Items</S.ExpandTH>
                            </tr>
                        </S.ExpandTHead>
                        <S.ExpandTBody>
                            {itemRows}
                            {_.isEmpty(itemRows) && (
                                <S.ExpandTR>
                                    <S.ExpandTD colSpan={3}>No items recorded</S.ExpandTD>
                                </S.ExpandTR>
                            )}
                        </S.ExpandTBody>
                    </S.ExpandTable>
                </Card.CdSectionCard>

                <Card.CdSectionCard $embedded>
                    <S.ExpandTable>
                        <S.ExpandTHead>
                            <tr>
                                <S.ExpandTH colSpan={2}>Transactions</S.ExpandTH>
                            </tr>
                        </S.ExpandTHead>
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTH>Date</S.ExpandTH>
                                <S.ExpandTH align="right">Amount</S.ExpandTH>
                                <S.ExpandTH>Refund</S.ExpandTH>
                                <S.ExpandTH>Billed by</S.ExpandTH>
                                <S.ExpandTH>Status</S.ExpandTH>
                                <S.ExpandTH>Memo</S.ExpandTH>
                            </S.ExpandTR>
                            {transactionRows}
                            {!transactions.length && (
                                <S.ExpandTR>
                                    <S.ExpandTD colSpan={2}>No transactions recorded</S.ExpandTD>
                                </S.ExpandTR>
                            )}
                        </S.ExpandTBody>
                    </S.ExpandTable>
                </Card.CdSectionCard>

                {onCreatePaymentPlan && (
                    <Card.CdSectionCard $embedded>
                        <S.StyledButton variant="primary" onClick={onCreatePaymentPlan}>
                            <S.StyledLightIcon path={mdiPlus} size={1} /> Create Payment Plan
                        </S.StyledButton>
                    </Card.CdSectionCard>
                )}
            </Card.CdPanel>
        );
    }

    // Support display: Simple invoice info and items
    return (
        <Card.CdPanel $embedded>
            <Card.CdSectionCard $embedded>
                <S.ExpandTable>
                <S.ExpandTBody>
                    <S.ExpandTR>
                        <S.ExpandTH>ID</S.ExpandTH>
                        <S.ExpandTD>{invoice.id}</S.ExpandTD>
                    </S.ExpandTR>
                    <S.ExpandTR>
                        <S.ExpandTH>Status</S.ExpandTH>
                        <S.ExpandTD>{invoice.invoiceStatus}</S.ExpandTD>
                    </S.ExpandTR>
                    <S.ExpandTR>
                        <S.ExpandTH>Invoice type</S.ExpandTH>
                        <S.ExpandTD>{invoice.invoiceType}</S.ExpandTD>
                    </S.ExpandTR>
                </S.ExpandTBody>
            </S.ExpandTable>
            </Card.CdSectionCard>

            {!!invoiceItems?.length && (
                <Card.CdSectionCard $embedded>
                    <S.Subtitle>Items</S.Subtitle>
                    <S.ListContainer>
                    {invoiceItems.map(item =>
                        isAdjustment(item) ? (
                            <S.ListItem key={item.id}>
                                <S.ExpandTable>
                                    <S.ExpandTBody>
                                        <S.ExpandTR>
                                            <S.ExpandTH>{renderAdjustmentLabel(item)}</S.ExpandTH>
                                            <S.ExpandTD>{renderAmount(item.price)}</S.ExpandTD>
                                        </S.ExpandTR>
                                    </S.ExpandTBody>
                                </S.ExpandTable>
                            </S.ListItem>
                        ) : (
                        <S.ListItem key={item.id}>
                            <S.ExpandTable>
                                <S.ExpandTBody>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Price</S.ExpandTH>
                                        <S.ExpandTD>{renderAmount(item.price)}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Quantity</S.ExpandTH>
                                        <S.ExpandTD>{item.quantity}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Tax</S.ExpandTH>
                                        <S.ExpandTD>{renderAmount(item.tax)}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Discount</S.ExpandTH>
                                        <S.ExpandTD>{renderAmount(item.discount)}</S.ExpandTD>
                                    </S.ExpandTR>
                                </S.ExpandTBody>
                            </S.ExpandTable>
                            {item.name && <div>{item.name}</div>}
                            {item.description && <div>{item.description}</div>}
                        </S.ListItem>
                        ),
                    )}
                    </S.ListContainer>
                </Card.CdSectionCard>
            )}
        </Card.CdPanel>
    );
};
